package kz.dentalcrm.app.screens.protocols.list

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawingPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.sizeIn
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import compose.icons.FeatherIcons
import compose.icons.feathericons.ChevronLeft
import compose.icons.feathericons.Edit2
import compose.icons.feathericons.Plus
import compose.icons.feathericons.Search
import compose.icons.feathericons.X
import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.empty_protocols
import dentalcrm.composeapp.generated.resources.protocols
import dentalcrm.composeapp.generated.resources.search
import kz.dentalcrm.app.core.ui.UiStateScaffold
import kz.dentalcrm.app.core.ui.components.ContentButton
import kz.dentalcrm.app.core.ui.components.UIEmptyListState
import kz.dentalcrm.app.core.ui.components.UIIconBackButton
import kz.dentalcrm.app.core.ui.components.UITextInput
import kz.dentalcrm.app.core.ui.components.lazy_column.PaginatedLazyColumn
import kz.dentalcrm.app.core.ui.components.refresh.PullToRefresh
import kz.dentalcrm.app.core.ui.components.refresh.rememberPullToRefreshState
import kz.dentalcrm.app.data.network.response_model.protocol_templates.ProtocolTemplate
import kz.dentalcrm.app.navigation.AppNavigator
import kz.dentalcrm.app.navigation.NavigationResults
import kz.dentalcrm.app.navigation.ProtocolDetailRoute
import kz.dentalcrm.app.navigation.resultFlow
import kz.dentalcrm.app.screens.protocols.add.AddProtocolDialog
import kz.dentalcrm.app.screens.protocols.edit.EditProtocolDialog
import kz.dentalcrm.app.screens.protocols.list.mvi.ProtocolsViewEvent
import kz.dentalcrm.app.screens.protocols.list.mvi.ProtocolsViewState
import kz.dentalcrm.app.screens.ui.ContentHeader
import kz.dentalcrm.app.theme.DefaultTheme
import kz.dentalcrm.app.theme.shimmer.ShimmerBounds
import kz.dentalcrm.app.theme.shimmer.rememberShimmer
import kz.dentalcrm.app.theme.shimmer.shimmer
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun ProtocolsScreen(
    viewModel: ProtocolsViewModel = koinViewModel()
) {
    val navigator: AppNavigator = koinInject()
    val results: NavigationResults = koinInject()

    LaunchedEffect(results) {
        results.resultFlow<ProtocolDetailRoute.Result>(ProtocolDetailRoute.RESULT_KEY)
            .collect { result ->
                if (result.updated) {
                    viewModel.handleEvents(ProtocolsViewEvent.Reload)
                }
                results.clear(ProtocolDetailRoute.RESULT_KEY)
            }
    }

    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.Center
    ) {
        val state by viewModel.viewState.collectAsState()

        UiStateScaffold(
            modifier = Modifier.fillMaxSize(),
            content = {
                ProtocolsContent(
                    state = state,
                    onBack = { navigator.navigateUp() },
                    onNavigateToDetail = { id -> navigator.navigate(ProtocolDetailRoute(id)) }
                ) { event ->
                    viewModel.handleEvents(event)
                }
            }
        )
    }
}

@Composable
private fun ProtocolsContent(
    state: ProtocolsViewState,
    onBack: () -> Unit,
    onNavigateToDetail: (String) -> Unit,
    event: (ProtocolsViewEvent) -> Unit
) {
    val isLoading = state.screenState is ProtocolsViewState.ScreenState.Loading
    val shimmer = rememberShimmer(ShimmerBounds.Window)
    val listState = rememberLazyListState()
    var isRefreshing by remember { mutableStateOf(false) }
    val pullToRefreshState = rememberPullToRefreshState(isRefreshing = isRefreshing)
    LaunchedEffect(isLoading) { if (!isLoading) isRefreshing = false }

    var showAddProtocolDialog by remember { mutableStateOf(false) }
    var showEditProtocolDialog by remember { mutableStateOf<ProtocolTemplate?>(null) }

    Box(
        modifier = Modifier.fillMaxSize()
            .safeDrawingPadding()
    ) {
        Column(modifier = Modifier.sizeIn()) {
            Spacer(modifier = Modifier.size(8.dp))
            ContentHeader(
                modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp),
                text = stringResource(Res.string.protocols),
                leadingContent = {
                    UIIconBackButton {
                        onBack()
                    }
                }
            )
            UITextInput(
                modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp),
                label = stringResource(Res.string.search),
                borderWidth = 1.dp,
                leadingIcon = FeatherIcons.Search,
                trailingIcon = FeatherIcons.X
            ) { search ->
                event(ProtocolsViewEvent.SearchProtocol(search))
            }
            Spacer(modifier = Modifier.size(12.dp))

            PullToRefresh(
                state = pullToRefreshState,
                onRefresh = { isRefreshing = true; event(ProtocolsViewEvent.Reload) },
                modifier = Modifier.fillMaxSize()
            ) {
                if (isLoading && state.treatments.isEmpty()) {
                    Column(modifier = Modifier.widthIn().padding(horizontal = 12.dp)) {
                        Spacer(modifier = Modifier.height(16.dp))
                        repeat(8) { TreatmentItemSkeleton(shimmer) }
                    }
                } else if (!isLoading && state.treatments.isEmpty()) {
                    UIEmptyListState(text = stringResource(Res.string.empty_protocols))
                } else {
                    val listModifier = if (isLoading)
                        Modifier.widthIn().padding(horizontal = 12.dp).shimmer(shimmer)
                    else
                        Modifier.widthIn().padding(horizontal = 12.dp)
                    PaginatedLazyColumn(
                        modifier = listModifier,
                        loadMoreItems = { event(ProtocolsViewEvent.LoadMore) },
                        listState = listState,
                        buffer = 5,
                        isLoading = isLoading,
                        paginatedTextError = (state.screenState as? ProtocolsViewState.ScreenState.Error)?.text,
                        retryOnPaginated = { event(ProtocolsViewEvent.LoadMore) }
                    ) {
                        item { Spacer(modifier = Modifier.height(16.dp)) }
                        items(state.treatments, key = { it.id }) { item ->
                            TreatmentItem(
                                item = item,
                                onEdit = { showEditProtocolDialog = item },
                                onClick = { onNavigateToDetail(item.id.toString()) }
                            )
                        }
                        item { Spacer(modifier = Modifier.height(80.dp)) }
                    }
                }
            }
        }
        FloatingActionButton(
            modifier = Modifier
                .navigationBarsPadding()
                .padding(bottom = 32.dp, start = 32.dp, end = 16.dp, top = 16.dp)
                .align(Alignment.BottomEnd),
            shape = RoundedCornerShape(50),
            containerColor = DefaultTheme.colors.primary,
            contentColor = DefaultTheme.colors.onPrimary,
            onClick = { showAddProtocolDialog = true }
        ) {
            Icon(imageVector = FeatherIcons.Plus, contentDescription = null)
        }
    }

    if (showAddProtocolDialog) {
        AddProtocolDialog(
            onAdd = { event(ProtocolsViewEvent.Reload) },
            onDismiss = { showAddProtocolDialog = false }
        )
    }

    showEditProtocolDialog?.let { protocol ->
        EditProtocolDialog(
            item = protocol,
            onEdit = { event(ProtocolsViewEvent.Reload) },
            onDelete = { id -> event(ProtocolsViewEvent.DeleteProtocol(id)) },
            onDismiss = { showEditProtocolDialog = null }
        )
    }
}

@Composable
private fun TreatmentItemSkeleton(shimmer: kz.dentalcrm.app.theme.shimmer.Shimmer) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(
                color = DefaultTheme.colors.primarySubdued,
                shape = RoundedCornerShape(16.dp)
            ),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Spacer(modifier = Modifier.width(12.dp))
        Column(modifier = Modifier.weight(1f)) {
            Spacer(modifier = Modifier.height(12.dp))
            Box(
                modifier = Modifier
                    .fillMaxWidth(0.55f)
                    .height(14.dp)
                    .shimmer(shimmer)
                    .background(
                        color = DefaultTheme.colors.onSurface.copy(alpha = 0.12f),
                        shape = RoundedCornerShape(4.dp)
                    )
            )
            Spacer(modifier = Modifier.height(4.dp))
            Box(
                modifier = Modifier
                    .fillMaxWidth(0.25f)
                    .height(12.dp)
                    .shimmer(shimmer)
                    .background(
                        color = DefaultTheme.colors.onSurface.copy(alpha = 0.12f),
                        shape = RoundedCornerShape(4.dp)
                    )
            )
            Spacer(modifier = Modifier.height(12.dp))
        }
        Spacer(modifier = Modifier.width(8.dp))
        // Edit button placeholder
        Box(
            modifier = Modifier
                .size(32.dp)
                .shimmer(shimmer)
                .background(
                    color = DefaultTheme.colors.onSurface.copy(alpha = 0.12f),
                    shape = RoundedCornerShape(8.dp)
                )
        )
        Spacer(modifier = Modifier.width(16.dp))
    }
    Spacer(Modifier.height(12.dp))
}

@Composable
fun TreatmentItem(
    item: ProtocolTemplate,
    onEdit: () -> Unit,
    onClick: () -> Unit,
) {
    ContentButton(
        modifier = Modifier,
        shape = RoundedCornerShape(16.dp),
        onClick = onClick
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .heightIn()
                .background(
                    color = DefaultTheme.colors.primarySubdued,
                    shape = RoundedCornerShape(16.dp)
                ),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Spacer(modifier = Modifier.width(12.dp))
            Column(modifier = Modifier.weight(1f)) {
                Spacer(modifier = Modifier.height(12.dp))
                Text(
                    text = item.name ?: "",
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    color = DefaultTheme.colors.onSurface,
                    style = DefaultTheme.typography.bodyMediumMedium,
                )
                Spacer(modifier = Modifier.height(4.dp))
                Text(
                    text = "ID: ${item.id}",
                    overflow = TextOverflow.Ellipsis,
                    maxLines = 2,
                    color = DefaultTheme.colors.onSurfaceVariant,
                    style = DefaultTheme.typography.bodySmallRegular,
                )
                Spacer(modifier = Modifier.height(12.dp))
            }
            Spacer(modifier = Modifier.width(8.dp))
            ContentButton(
                modifier = Modifier.size(32.dp),
                shape = RoundedCornerShape(8.dp),
                onClick = onEdit
            ) {
                Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        modifier = Modifier.size(16.dp),
                        imageVector = FeatherIcons.Edit2,
                        tint = DefaultTheme.colors.onSurface,
                        contentDescription = null
                    )
                }
            }
            Spacer(modifier = Modifier.width(16.dp))
        }
    }
    Spacer(Modifier.height(12.dp))
}
